import { useMemo, useRef } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { Task } from '@/src/renderer/lib/db/models'
import type { ListedTask } from '../utils/taskToEntryData'
import { isWholeChain, parentKeepsChildListed } from '@/src/renderer/lib/db/taskFilters'

/** Das übliche `keep`: Die Unteraufgabe steht da, solange jede Ebene darüber offen, wach und da ist. */
export const keepWhileParentListed = (_child: Task, parent: Task) => parentKeepsChildListed(parent)

/**
 * **Unteraufgaben außerhalb ihrer Checkliste** (#205): Ruhend, Wiederkehrend und Geschafft
 * zeigen eine Unteraufgabe mit dem Titel ihrer Aufgabe davor — ob überhaupt, entscheidet
 * `keep`, und zwar an **jeder Ebene darüber** (#181): Ruht eine davon, steht die tiefe Zeile
 * nicht da. Davor steht die Aufgabe ganz oben — dort findet man sie. Aufgaben ohne Elternteil
 * gehen unverändert durch. `undefined`, bis die Ketten geladen sind.
 *
 * Dieselbe Zeile bleibt dasselbe Objekt, solange sich weder sie noch Titel, Wiederholung und
 * Tiefe ihrer Kette ändern — die Listen memoisieren darauf.
 *
 * @param keep muss stabil sein (Modulebene oder `useCallback`) — es steht in den Abhängigkeiten
 */
export function useListedChildren(
  tasks: readonly Task[] | undefined,
  keep: (child: Task, parent: Task) => boolean,
): ListedTask[] | undefined {
  const parentIds = useMemo(
    () => [...new Set((tasks ?? []).flatMap(t => (t.parentTaskId ? [t.parentTaskId] : [])))].sort(),
    [tasks],
  )
  const chains = useDataSource().useTaskChains(parentIds)
  const cache = useRef(new WeakMap<Task, ListedTask>())

  return useMemo(() => {
    if (!tasks) return undefined
    if (parentIds.length > 0 && !chains) return undefined
    return tasks.flatMap((task): ListedTask[] => {
      if (!task.parentTaskId) return [task]
      const chain = chains?.get(task.parentTaskId)
      if (!chain || !isWholeChain(chain) || !chain.every(ancestor => keep(task, ancestor))) return []
      const parentTitle = chain[chain.length - 1]!.title
      const parentRecurring = chain.some(ancestor => ancestor.recurrence != null)
      const depth = chain.length
      const cached = cache.current.get(task)
      if (cached?.parentTitle === parentTitle && cached.parentRecurring === parentRecurring && cached.depth === depth) return [cached]
      const listed: ListedTask = { ...task, parentTitle, parentRecurring, depth }
      cache.current.set(task, listed)
      return [listed]
    })
  }, [tasks, chains, parentIds, keep])
}
